import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import StepChoose from "./StepChoose";
import StepUpload from "./StepUpload";
import StepMapColumns from "./StepMapColumns";
import StepReview from "./StepReview";
import { BULK_CONNECT_STEPS } from "./constants";
import {
  parseCsvText,
  buildAutoMapping,
  mapRowsToObjects,
  applyRowDefaults,
  validateRow,
  buildBackendRowPayload
} from "./csvUtils";

// Orchestrates the "Bulk import via CSV" wizard (Upload -> Map Columns ->
// Review & Connect -> Import Complete). "Single account" continues to use
// the existing ConnectSmtpImap.js flow untouched, via onSingleAccount().
const BulkCsvConnect = (props = {}) => {
  const {
    onClose = () => {},
    onSingleAccount = () => {},
    reloadInbox = () => {}
  } = props || {};

  const history = useHistory();
  const [step, setStep] = useState(BULK_CONNECT_STEPS.CHOOSE);
  const [rawMapping, setRawMapping] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [reviewRows, setReviewRows] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");

  const handleClose = () => {
    onClose();
  };

  const handleUploadNext = (csvText, fileName) => {
    const { headers, rows } = parseCsvText(csvText);
    setRawMapping(buildAutoMapping(headers));
    setRawRows(rows);
    setCsvFileName(fileName || "");
    setStep(BULK_CONNECT_STEPS.MAP);
  };

  const handleMapNext = mapping => {
    const mappedRows = mapRowsToObjects(rawRows, mapping)
      .map(applyRowDefaults)
      .map(validateRow);
    setReviewRows(mappedRows);
    setStep(BULK_CONNECT_STEPS.REVIEW);
  };

  const handleReviewConnect = async readyRows => {
    setConnecting(true);
    try {
      const config = {
        url: URLS.connectSmtpBulk,
        method: "POST",
        data: { rows: readyRows.map(buildBackendRowPayload), fileName: csvFileName }
      };
      const response = await makeApi(config);
      const { jobId: newJobId, status, async: isAsyncFlag, jobStatus } = response?.data || {};
      // Trust the explicit async flag, but fall back to jobStatus so a background job is
      // never mistaken for a completed one if that field is ever missing - showing "connected
      // successfully" for a job that hasn't run yet would be actively misleading.
      const isAsync = Boolean(isAsyncFlag) || jobStatus === "TO_BE_PROCESSED";
      if (status && newJobId) {
        // Neither path shows a separate "X connected" summary screen - just the one
        // slide-down popup, then get out of the way. Background jobs aren't connected
        // yet, so the inbox list isn't refreshed until the user checks Inbox Imports;
        // synchronous jobs are already done, so the list is refreshed immediately.
        handleClose();
        if (isAsync) {
          confirmationPopupState.setPopupValues({
            title: "Import scheduled",
            content:
              "Your inboxes are being connected. You can track the progress in Settings --> Imports/Exports.",
            actionBtnText: "Go to Imports/Exports",
            cancelBtnText: "OK",
            needCancelBtn: true,
            callback: () => {
              confirmationPopupState.setShowPopup(false);
              history.push("/settings/imports/bulk-actions");
            },
            cancelCbk: () => confirmationPopupState.setShowPopup(false)
          });
        } else {
          reloadInbox(true);
          confirmationPopupState.setPopupValues({
            title: "Inboxes connected",
            content: "Your inboxes have been connected successfully.",
            actionBtnText: "OK",
            needCancelBtn: false,
            callback: () => confirmationPopupState.setShowPopup(false)
          });
        }
        confirmationPopupState.setShowPopup(true);
      } else {
        toasterState.setToastMsg(
          response?.data?.message ||
            "Something went wrong while connecting your inboxes. Please try again.",
          "fail"
        );
      }
    } catch (error) {
      console.error("Bulk connect smtp inboxes failed", error);
      toasterState.setToastMsg(
        error?.response?.data?.message ||
          "Something went wrong while connecting your inboxes. Please try again.",
        "fail"
      );
    } finally {
      setConnecting(false);
    }
  };

  if (step === BULK_CONNECT_STEPS.CHOOSE) {
    return (
      <StepChoose
        onClose={handleClose}
        onSingle={onSingleAccount}
        onBulk={() => setStep(BULK_CONNECT_STEPS.UPLOAD)}
      />
    );
  }
  if (step === BULK_CONNECT_STEPS.UPLOAD) {
    return (
      <StepUpload
        onClose={handleClose}
        onBack={() => setStep(BULK_CONNECT_STEPS.CHOOSE)}
        onNext={handleUploadNext}
      />
    );
  }
  if (step === BULK_CONNECT_STEPS.MAP) {
    return (
      <StepMapColumns
        onClose={handleClose}
        onBack={() => setStep(BULK_CONNECT_STEPS.UPLOAD)}
        onNext={handleMapNext}
        initialMapping={rawMapping}
      />
    );
  }
  if (step === BULK_CONNECT_STEPS.REVIEW) {
    return (
      <StepReview
        onClose={handleClose}
        onBack={() => setStep(BULK_CONNECT_STEPS.MAP)}
        onNext={handleReviewConnect}
        rows={reviewRows}
        connecting={connecting}
      />
    );
  }
  return null;
};

export default BulkCsvConnect;
export { BulkCsvConnect };
